/**
 * useRegulatorAPI — Zustand store that connects to the ARS93 REST/WebSocket API.
 *
 * Opens a WebSocket to /ws, receives state_update messages,
 * and exposes an interface compatible with useDetailedSimulation.
 */

import { create } from 'zustand';

// ── Types ─────────────────────────────────────────────────────────

interface GroupState {
  number: number;
  pcode: number;
  symbol: string;
}

interface DriverState {
  timestamp: string;
  connected: boolean;
  logical_id: number;
  protocol: string;
  transport: string;
  state: number;
  state_name: string;
  plan: number;
  phase: string;
  cycle_time: number;
  cycle_elapsed: number;
  phase_elapsed: number;
  groups: GroupState[];
  group_count: number;
  detectors: boolean[];
  detector_count: number;
  alarms: string[];
  event_count: number;
  fallback_active: boolean;
  hurry_active: boolean;
}

interface RegulatorAPIState {
  // Connection
  connected: boolean;
  wsUrl: string;
  apiUrl: string;
  activeDriverId: number | null;

  // Driver states (keyed by logical_id string)
  drivers: Record<string, DriverState>;

  // Derived state (from active driver, compatible with simulation store)
  currentPhase: string;
  cyclePosition: number;
  cycleTime: number;
  isRunning: boolean;
  speed: number;
  regulatorState: number;
  currentPlan: number;
  activeAlarms: string[];

  // Actions
  connect: (apiBaseUrl: string) => void;
  disconnect: () => void;
  setActiveDriver: (driverId: number) => void;
  sendCommand: (driverId: number, command: string) => Promise<string>;
}

// ── Store ─────────────────────────────────────────────────────────

let ws: WebSocket | null = null;
let pingInterval: ReturnType<typeof setInterval> | null = null;

export const useRegulatorAPI = create<RegulatorAPIState>((set, get) => ({
  connected: false,
  wsUrl: '',
  apiUrl: '',
  activeDriverId: null,
  drivers: {},

  // Derived defaults
  currentPhase: '-',
  cyclePosition: 0,
  cycleTime: 0,
  isRunning: false,
  speed: 1,
  regulatorState: 0,
  currentPlan: 0,
  activeAlarms: [],

  connect: (apiBaseUrl: string) => {
    const state = get();
    if (state.connected) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl: string;
    let apiUrl: string;

    if (apiBaseUrl.startsWith('http')) {
      // Explicit URL: http://host:port → ws://host:port/ws
      apiUrl = apiBaseUrl;
      const url = new URL(apiBaseUrl);
      wsUrl = `${wsProtocol}//${url.host}/ws`;
    } else {
      // Relative: use current origin (via vite proxy)
      apiUrl = '';
      wsUrl = `${wsProtocol}//${window.location.host}/ws`;
    }

    set({ wsUrl, apiUrl });

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        set({ connected: true });

        // Start ping every 30s
        pingInterval = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'state_update' && data.drivers) {
            const drivers: Record<string, DriverState> = data.drivers;
            set({ drivers });

            // Update derived state from active driver
            const { activeDriverId } = get();
            if (activeDriverId !== null) {
              const active = drivers[String(activeDriverId)];
              if (active) {
                set({
                  currentPhase: active.phase || '-',
                  cyclePosition: active.cycle_elapsed || 0,
                  cycleTime: active.cycle_time || 0,
                  isRunning: active.connected,
                  regulatorState: active.state,
                  currentPlan: active.plan,
                  activeAlarms: active.alarms || [],
                });
              }
            }
          }
        } catch {
          // Ignore parse errors
        }
      };

      ws.onclose = () => {
        set({ connected: false });
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
        ws = null;
      };

      ws.onerror = () => {
        // onclose will follow
      };
    } catch {
      set({ connected: false });
    }
  },

  disconnect: () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    if (ws) {
      ws.close();
      ws = null;
    }
    set({ connected: false, drivers: {} });
  },

  setActiveDriver: (driverId: number) => {
    set({ activeDriverId: driverId });
    // Immediately update derived state if we have data
    const { drivers } = get();
    const active = drivers[String(driverId)];
    if (active) {
      set({
        currentPhase: active.phase || '-',
        cyclePosition: active.cycle_elapsed || 0,
        cycleTime: active.cycle_time || 0,
        isRunning: active.connected,
        regulatorState: active.state,
        currentPlan: active.plan,
        activeAlarms: active.alarms || [],
      });
    }
  },

  sendCommand: async (driverId: number, command: string): Promise<string> => {
    const { apiUrl } = get();
    const url = `${apiUrl}/api/drivers/${driverId}/command`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command }),
    });
    const data = await resp.json();
    return data.response || data.error || '';
  },
}));
